/**
 * statusHistory models (VE-26730): types and pure functions for the
 * `redact-tdo-status-history` SDO — one append-only record per redaction-status
 * transition on a TDO, plus one seeded record at file creation.
 *
 * Everything here is pure so the mapping, normalization, and page-merge
 * invariants are property-testable with fast-check. No I/O, no store access,
 * and no clock: the only time a row carries is the one the platform stamps on
 * the record itself.
 */
import {
  isRedactionStatusFilter,
  resolveRedactionStatusKey,
} from '@common-modules/mediaDetails/redactionStatuses';

/**
 * Upper bound on a note, raised from the original 300 because that was cramping
 * real correction instructions (a reviewer listing four things to fix on three
 * pages runs past it easily). Deliberately still a bound rather than none: the
 * note is one field of an SDO record written on the status-change path, so an
 * unbounded paste turns a status change into a multi-megabyte write. 4000 is
 * roughly a page of prose — past any legitimate instruction, short of anything
 * that can hurt the write.
 */
export const STATUS_HISTORY_NOTE_MAX_LENGTH = 4000;
export const STATUS_HISTORY_PAGE_SIZE = 50;

/** Raw SDO `data` payload — the persisted contract (schema field names). */
export interface StatusHistorySdoData {
  readonly tdoId: string;
  /** Email of the acting user (audit-event payload convention). */
  readonly userName: string;
  /** Display label frozen at write time (deleted custom statuses keep rendering it). */
  readonly statusLabel: string;
  /** Canonical key for built-ins ('to do', …), uuid for custom statuses. */
  readonly statusKey: string;
  readonly isCustomStatus: boolean;
  /** True for programmatic transitions (auto-promote, lock replay) and baseline rows. */
  readonly isSystem: boolean;
  /** Optional correction note (Correction Needed transitions only). */
  readonly note?: string;
  /**
   * Free-text reply to the note this file was carrying, written on the
   * transition that answers it (never on the one that raised it). Always
   * optional, whatever the outcome — the reply people actually type is worth
   * reading precisely because nothing forces it.
   */
  readonly response?: string;
  /**
   * What the user said about the note when asked, on the same outgoing row:
   *
   * - 'addressed'     — they confirmed the correction was made.
   * - 'not_addressed' — they decided the note would NOT be acted on.
   * - 'skipped'       — they were asked and moved on without answering.
   *
   * Absent means they were never asked: the file had no open note, or the
   * transition predates this gate. Distinguishing that from 'skipped' matters —
   * one is silence from the system, the other is silence from a person.
   *
   * Spelled out rather than shortened because this is the field an auditor
   * reads raw, years later, without the schema description to hand.
   */
  readonly responseType?: StatusHistoryNoteOutcome;
  /**
   * Present only on a row that OPENS a history, and says how it opened:
   *
   * - 'created' — the file's genuine first status, written at TDO creation.
   *   Everything that ever happened to this file is in the history.
   * - 'adopted' — the file already had a status when Redact first saw it, so
   *   the history starts mid-life and this row is the join point.
   * - 'cleared' — the rows before this one were soft-deleted by a user, and
   *   this row is the tombstone recording that.
   *
   * Absent on every other row — and on the oldest row of a history written
   * before this field existed, or of a file that predates the feature entirely.
   * That absence is itself the signal: no origin row means the depth of what
   * came before is unknown, so `absent` and `'adopted'` read the same way and
   * only `'created'` licenses calling a history complete. See
   * `selectStatusHistoryCompleteness`.
   */
  readonly origin?: StatusHistoryOrigin;
  /**
   * Soft delete. A cleared row keeps its record and stops being shown — nothing
   * is removed server-side, matching how comments already handle this
   * (`deleted` on the comment SDO). A trail should not be destroyed to be
   * tidied, and un-setting the flag brings the row back.
   *
   * Only ever written `true`; absent is the live state.
   */
  readonly isDeleted?: boolean;
  /**
   * Reserved. Declared so a future note-only row (commentary with no status
   * change) is an additive read, not a migration — absent means 'transition',
   * which every row written today is. Never written yet.
   */
  readonly rowType?: StatusHistoryRowType;
}

export type StatusHistoryOrigin = 'created' | 'adopted' | 'cleared';

export type StatusHistoryRowType = 'transition' | 'note';

export type StatusHistoryNoteOutcome =
  | 'addressed'
  | 'not_addressed'
  | 'skipped';

export const STATUS_HISTORY_NOTE_OUTCOMES: readonly StatusHistoryNoteOutcome[] =
  ['addressed', 'not_addressed', 'skipped'];

const isRowType = (value: unknown): value is StatusHistoryRowType =>
  value === 'transition' || value === 'note';

const isOrigin = (value: unknown): value is StatusHistoryOrigin =>
  value === 'created' || value === 'adopted' || value === 'cleared';

const isNoteOutcome = (value: unknown): value is StatusHistoryNoteOutcome =>
  typeof value === 'string' &&
  (STATUS_HISTORY_NOTE_OUTCOMES as readonly string[]).includes(value);

/**
 * How much of a file's life the history covers. 'unknown' is not an error
 * state — it is the honest answer for a legacy history and for one that has
 * not been paged to its oldest row yet.
 *
 * 'cleared' is kept apart from 'partial' for the UI's sake: the tombstone row
 * already states the boundary, with who cleared it and when, so a footer
 * saying the same thing in smaller type is noise.
 */
export type StatusHistoryCompleteness =
  | 'complete'
  | 'partial'
  | 'cleared'
  | 'unknown';

/** An SDO record mapped for UI use. */
export interface StatusHistoryRecord extends StatusHistorySdoData {
  readonly id: string;
  /**
   * Server-side creation time — the sort key for display ordering AND the time
   * shown to users. The platform assigns it, so a normal write cannot forge it
   * — which is why the app stores no time of its own in the row.
   */
  readonly createdDateTime: string;
}

/** Internal watcher → writer contract. */
export interface RecordStatusChangeInput {
  readonly tdoId: string;
  readonly statusLabel: string;
  readonly statusKey: string;
  readonly isCustomStatus: boolean;
  readonly isSystem: boolean;
  readonly note?: string;
  /**
   * The user's answer about the note the file is carrying, captured when they
   * transition OUT of the status that raised it (VE-26730). Both land on the
   * OUTGOING row — the transition that answers the note, not the one that
   * raised it.
   */
  readonly response?: string;
  readonly responseType?: StatusHistoryNoteOutcome;
  /** Set only by the two seeding paths; see StatusHistorySdoData.origin. */
  readonly origin?: StatusHistoryOrigin;
  readonly rowType?: StatusHistoryRowType;
}

export interface StatusHistoryPage {
  readonly records: readonly StatusHistoryRecord[];
  readonly count: number;
  readonly isLoading: boolean;
  readonly error: string | null;
}

export type StatusHistoryGate = 'unknown' | 'enabled' | 'disabled';

export interface StatusHistoryState {
  readonly gate: StatusHistoryGate;
  readonly schemaId: string | null;
  /** undefined = not fetched; null = fetched and the TDO has no rows. */
  readonly latestByTdoId: Record<string, StatusHistoryRecord | null>;
  readonly historyByTdoId: Record<string, StatusHistoryPage>;
  /**
   * TDOs this session has read-verified via a server-acknowledged fetch of the
   * TDO itself (SECURITY-11). The read sagas drop every fetch for a TDO not in
   * this set — the SDO read is schema-level authorized, so this funnel is the
   * client-side access control. Monotonic for the session.
   */
  readonly readVerifiedTdoIds: Record<string, true>;
}

/** Trims and bounds a note or response; empty/whitespace/non-string becomes undefined. */
export const normalizeNote = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }
  return trimmed.length > STATUS_HISTORY_NOTE_MAX_LENGTH
    ? trimmed.slice(0, STATUS_HISTORY_NOTE_MAX_LENGTH)
    : trimmed;
};

/**
 * Status fields from a persisted `in redaction` TDO tag — the authoritative
 * source after a transition succeeds (the tag is what the mutation wrote).
 */
export const deriveStatusFieldsFromTag = (tag: {
  readonly redactionStatus?: unknown;
  readonly redactionStatusKey?: unknown;
}): Pick<
  RecordStatusChangeInput,
  'statusLabel' | 'statusKey' | 'isCustomStatus'
> | null => {
  const label =
    typeof tag.redactionStatus === 'string' && tag.redactionStatus.trim()
      ? tag.redactionStatus.trim()
      : undefined;
  const customKey =
    typeof tag.redactionStatusKey === 'string' && tag.redactionStatusKey.trim()
      ? tag.redactionStatusKey.trim()
      : undefined;
  if (customKey) {
    return {
      statusLabel: label ?? customKey,
      statusKey: customKey,
      isCustomStatus: true,
    };
  }
  const key = resolveRedactionStatusKey(label);
  if (!key || !label) {
    return null;
  }
  return {
    statusLabel: label,
    statusKey: key,
    isCustomStatus: !isRedactionStatusFilter(key),
  };
};

/**
 * Status fields from a picker selection / status action payload — the fallback
 * when a success action does not echo the written tag.
 */
export const deriveStatusFieldsFromSelection = (selection: {
  readonly status?: unknown;
  readonly statusLabel?: unknown;
  readonly statusKey?: unknown;
}): Pick<
  RecordStatusChangeInput,
  'statusLabel' | 'statusKey' | 'isCustomStatus'
> | null => {
  const explicitKey =
    typeof selection.statusKey === 'string' && selection.statusKey.trim()
      ? selection.statusKey.trim()
      : undefined;
  const label =
    typeof selection.statusLabel === 'string' && selection.statusLabel.trim()
      ? selection.statusLabel.trim()
      : typeof selection.status === 'string' && selection.status.trim()
        ? selection.status.trim()
        : undefined;
  const key = explicitKey ?? resolveRedactionStatusKey(label);
  if (!key || !label) {
    return null;
  }
  return {
    statusLabel: label,
    statusKey: key,
    isCustomStatus: !isRedactionStatusFilter(resolveRedactionStatusKey(key)),
  };
};

/** Builds the SDO data for one transition; `nowIso` injected for testability. */
export const mapStatusChangeToSdoData = (
  input: RecordStatusChangeInput,
  userName: string
): StatusHistorySdoData => {
  const note = normalizeNote(input.note);
  const response = normalizeNote(input.response);
  return {
    tdoId: input.tdoId,
    userName,
    statusLabel: input.statusLabel,
    statusKey: input.statusKey,
    isCustomStatus: input.isCustomStatus,
    isSystem: input.isSystem,
    ...(note ? { note } : {}),
    ...(response ? { response } : {}),
    ...(input.responseType ? { responseType: input.responseType } : {}),
    ...(input.origin ? { origin: input.origin } : {}),
    ...(input.rowType ? { rowType: input.rowType } : {}),
  };
};

interface SdoRecordLike {
  readonly id?: unknown;
  readonly createdDateTime?: unknown;
  readonly data?: unknown;
}

/**
 * Maps one raw SDO record to the read model; returns null for malformed rows
 * so a bad record can never crash the UI (callers drop nulls).
 */
export const mapSdoRecordToStatusHistoryRecord = (
  record: SdoRecordLike | null | undefined
): StatusHistoryRecord | null => {
  if (!record || typeof record.id !== 'string' || !record.id) {
    return null;
  }
  const data =
    record.data && typeof record.data === 'object'
      ? (record.data as Record<string, unknown>)
      : null;
  if (!data || typeof data['tdoId'] !== 'string' || !data['tdoId']) {
    return null;
  }
  const statusLabel =
    typeof data['statusLabel'] === 'string' ? data['statusLabel'] : '';
  const statusKey =
    typeof data['statusKey'] === 'string' && data['statusKey']
      ? data['statusKey']
      : (resolveRedactionStatusKey(statusLabel) ?? '');
  const rowType = isRowType(data['rowType']) ? data['rowType'] : undefined;
  // A clear tombstone records no status — nothing transitioned — so the
  // status-fields guard below must not throw it away.
  const isClearTombstone = data['origin'] === 'cleared';
  if (!statusLabel && !statusKey && !isClearTombstone) {
    return null;
  }
  return {
    id: record.id,
    createdDateTime:
      typeof record.createdDateTime === 'string' ? record.createdDateTime : '',
    tdoId: data['tdoId'],
    userName: typeof data['userName'] === 'string' ? data['userName'] : '',
    statusLabel: statusLabel || statusKey,
    statusKey,
    isCustomStatus: data['isCustomStatus'] === true,
    isSystem: data['isSystem'] === true,
    ...(typeof data['note'] === 'string' && data['note'].trim()
      ? { note: data['note'] }
      : {}),
    ...(typeof data['response'] === 'string' && data['response'].trim()
      ? { response: data['response'] }
      : {}),
    ...(isNoteOutcome(data['responseType'])
      ? { responseType: data['responseType'] }
      : {}),
    ...(isOrigin(data['origin']) ? { origin: data['origin'] } : {}),
    ...(rowType ? { rowType } : {}),
    ...(data['isDeleted'] === true ? { isDeleted: true } : {}),
  };
};

const byCreatedDateTimeDesc = (
  a: StatusHistoryRecord,
  b: StatusHistoryRecord
): number => {
  if (a.createdDateTime === b.createdDateTime) {
    // Stable tie-break so merge order can't flip rows written the same second.
    return a.id < b.id ? 1 : a.id > b.id ? -1 : 0;
  }
  return a.createdDateTime < b.createdDateTime ? 1 : -1;
};

/**
 * Merges a fetched page into the already-loaded records: unique by id (the
 * incoming copy wins), sorted createdDateTime-desc. Idempotent — appending the
 * same page twice is the same as once.
 */
export const mergeHistoryPage = (
  existing: readonly StatusHistoryRecord[],
  incoming: readonly StatusHistoryRecord[]
): StatusHistoryRecord[] => {
  const byId = new Map<string, StatusHistoryRecord>();
  for (const record of existing) {
    byId.set(record.id, record);
  }
  for (const record of incoming) {
    byId.set(record.id, record);
  }
  return Array.from(byId.values()).sort(byCreatedDateTimeDesc);
};
